import Joi, { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { RentalReservationRecordDto } from '../../dtos/rental-reservation-record.dto';
import { RentalReservationActionEnum } from '../../enums/rental-reservation-action.enum';
import { RentalReservationStatusEnum } from '../../enums/rental-reservation-status.enum';
import { setReservationStatusSchema } from '../schemas/set-reservation-status.schema';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: RentalReservationRecordDto,
	action: RentalReservationActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	switch (action) {
		case RentalReservationActionEnum.ACCEPT:
			validationResult = setReservationStatusSchema(
				RentalReservationStatusEnum.ACCEPTED,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalReservationActionEnum.REJECT:
			validationResult = setReservationStatusSchema(
				RentalReservationStatusEnum.REJECTED,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalReservationActionEnum.PENDING:
			validationResult = setReservationStatusSchema(
				RentalReservationStatusEnum.PENDING,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalReservationActionEnum.SEND_PAYMENT_LINK:
			validationResult = Joi.object({
				status: Joi.string()
					.valid(RentalReservationStatusEnum.PENDING)
					.required(),
			})
				.unknown(true)
				.validate(record, { abortEarly });
			break;

		default:
			validationResult = { error: undefined, value: record };
			break;
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
